import { RefObject, useEffect, useState } from 'react';

interface MatchesMenuPositionType {
    top: number;
    left: number;
    minWidth: number;
}

interface UseUnifiedMatchesDropdownPositionPropsType {
    isOpen: boolean;
    buttonRef: RefObject<HTMLDivElement>;
    menuRef: RefObject<HTMLDivElement>;
    onClose: () => void;
}

interface UseUnifiedMatchesDropdownPositionResultType {
    menuPosition: MatchesMenuPositionType | null;
    updateMenuPosition: () => void;
}

export const useUnifiedMatchesDropdownPosition = (
    props: UseUnifiedMatchesDropdownPositionPropsType
): UseUnifiedMatchesDropdownPositionResultType => {
    const { isOpen, buttonRef, menuRef, onClose } = props;
    const [menuPosition, setMenuPosition] = useState<MatchesMenuPositionType | null>(null);

    const updateMenuPosition = (): void => {
        if (buttonRef.current === null) {
            return;
        }

        const rect = buttonRef.current.getBoundingClientRect();
        setMenuPosition({
            top: rect.bottom + 4,
            left: rect.left,
            minWidth: Math.max(rect.width, 160),
        });
    };

    useEffect(() => {
        if (isOpen === false) {
            return;
        }

        updateMenuPosition();

        const handleClickOutside = (event: MouseEvent): void => {
            const target = event.target as Node;
            const isButtonClick = buttonRef.current?.contains(target) === true;
            const isMenuClick = menuRef.current?.contains(target) === true;

            if (isButtonClick || isMenuClick) {
                return;
            }

            onClose();
        };

        document.addEventListener('click', handleClickOutside);
        window.addEventListener('resize', updateMenuPosition);
        window.addEventListener('scroll', updateMenuPosition, true);

        return (): void => {
            document.removeEventListener('click', handleClickOutside);
            window.removeEventListener('resize', updateMenuPosition);
            window.removeEventListener('scroll', updateMenuPosition, true);
        };
    }, [isOpen, buttonRef, menuRef, onClose]);

    return {
        menuPosition,
        updateMenuPosition,
    };
};
